import { useCallback, useEffect, useState } from 'react'
import { Plus, Users } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchBranches } from '@/lib/api/branches'
import { createUser, fetchRoles, fetchUsers, updateUser } from '@/lib/api/users'
import { PHONE_LOCAL_PLACEHOLDER } from '@/lib/phone'
import { cn } from '@/lib/utils'

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function emptyForm(branches, roles) {
  const defaultBranchId = branches[0]?.id ?? null
  return {
    name: '',
    email: '',
    phone: '',
    password: '',
    role_id: roles[0]?.id ?? null,
    default_branch_id: defaultBranchId,
    branch_ids: defaultBranchId ? [defaultBranchId] : [],
    is_active: true,
  }
}

function userToForm(user) {
  return {
    name: user.name ?? '',
    email: user.email ?? '',
    phone: user.phone ?? '',
    password: '',
    role_id: user.role_id ?? user.role?.id ?? null,
    default_branch_id: user.default_branch_id ?? user.default_branch?.id ?? null,
    branch_ids: user.branches?.map((branch) => branch.id) ?? [],
    is_active: Boolean(user.is_active),
  }
}

export function UsersSetupPage() {
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role_id: null,
    default_branch_id: null,
    branch_ids: [],
    is_active: true,
  })

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [userItems, roleItems, branchItems] = await Promise.all([
        fetchUsers(),
        fetchRoles(),
        fetchBranches(),
      ])
      setUsers(userItems)
      setRoles(roleItems)
      setBranches(branchItems)
      return { userItems, roleItems, branchItems }
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load users.')
      return { userItems: [], roleItems: [], branchItems: [] }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    async function init() {
      const { userItems, roleItems, branchItems } = await loadData()
      if (!active) return

      if (userItems.length > 0) {
        setSelectedId(userItems[0].id)
        setForm(userToForm(userItems[0]))
        setCreating(false)
      } else {
        setCreating(true)
        setForm(emptyForm(branchItems, roleItems))
      }
    }

    init()
    return () => {
      active = false
    }
  }, [loadData])

  function selectUser(user) {
    setCreating(false)
    setSelectedId(user.id)
    setForm(userToForm(user))
    setSuccess('')
    setError('')
  }

  function startCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm(branches, roles))
    setSuccess('')
    setError('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  function toggleBranch(branchId) {
    setForm((current) => {
      const hasBranch = current.branch_ids.includes(branchId)
      const branchIds = hasBranch
        ? current.branch_ids.filter((id) => id !== branchId)
        : [...current.branch_ids, branchId]

      let defaultBranchId = current.default_branch_id
      if (!branchIds.includes(defaultBranchId)) {
        defaultBranchId = branchIds[0] ?? null
      }

      return {
        ...current,
        branch_ids: branchIds,
        default_branch_id: defaultBranchId,
      }
    })
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone || null,
      role_id: form.role_id,
      default_branch_id: form.default_branch_id,
      branch_ids: form.branch_ids,
      is_active: form.is_active,
    }

    if (creating) {
      payload.password = form.password
    } else if (form.password) {
      payload.password = form.password
    }

    try {
      if (creating) {
        const created = await createUser(payload)
        const { userItems } = await loadData()
        const saved = userItems.find((item) => item.id === created.id) ?? created
        selectUser(saved)
        setSuccess('User created.')
      } else if (selectedId) {
        const updated = await updateUser(selectedId, payload)
        setUsers((current) => current.map((item) => (item.id === updated.id ? updated : item)))
        setForm(userToForm(updated))
        setSuccess('User saved.')
      }
    } catch (err) {
      const message =
        getFieldError(err, 'email') ??
        getFieldError(err, 'password') ??
        getFieldError(err, 'role_id') ??
        getFieldError(err, 'branch_ids') ??
        err?.response?.data?.message ??
        'Unable to save user.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Users className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Users & roles</h1>
            <p className="text-sm text-muted-foreground">
              Staff accounts, role assignment, and branch access.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={startCreate}>
          <Plus className="h-4 w-4" />
          New user
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="card-surface p-3">
          <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            Team
          </p>
          {loading ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-1">
              {users.map((user) => {
                const isActive = !creating && selectedId === user.id
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => selectUser(user)}
                    className={cn(
                      'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm transition-colors',
                      isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/80',
                    )}
                  >
                    <span className="font-medium">{user.name}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {user.role?.name ?? 'No role'}
                    </span>
                  </button>
                )
              })}
              {creating ? (
                <div className="rounded-xl bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
                  New user
                </div>
              ) : null}
            </div>
          )}
        </aside>

        <form onSubmit={handleSubmit} className="card-surface p-6 lg:p-8">
          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="user-name" className="mb-1.5 block text-sm font-medium">
                  Full name
                </label>
                <input
                  id="user-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
              <div>
                <label htmlFor="user-email" className="mb-1.5 block text-sm font-medium">
                  Email
                </label>
                <input
                  id="user-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="user-phone" className="mb-1.5 block text-sm font-medium">
                  Phone
                </label>
                <input
                  id="user-phone"
                  type="text"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder={PHONE_LOCAL_PLACEHOLDER}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
              <div>
                <label htmlFor="user-role" className="mb-1.5 block text-sm font-medium">
                  Role
                </label>
                <select
                  id="user-role"
                  value={form.role_id ?? ''}
                  onChange={(e) => updateField('role_id', Number(e.target.value))}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  required
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="user-password" className="mb-1.5 block text-sm font-medium">
                {creating ? 'Password' : 'New password (optional)'}
              </label>
              <input
                id="user-password"
                type="password"
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required={creating}
              />
            </div>

            <div>
              <p className="mb-2 text-sm font-medium">Branch access</p>
              <div className="space-y-2">
                {branches.map((branch) => (
                  <label key={branch.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.branch_ids.includes(branch.id)}
                      onChange={() => toggleBranch(branch.id)}
                      className="rounded border-border"
                    />
                    <span>{branch.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="user-default-branch" className="mb-1.5 block text-sm font-medium">
                Default branch
              </label>
              <select
                id="user-default-branch"
                value={form.default_branch_id ?? ''}
                onChange={(e) => updateField('default_branch_id', Number(e.target.value))}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              >
                {form.branch_ids.map((branchId) => {
                  const branch = branches.find((item) => item.id === branchId)
                  if (!branch) return null
                  return (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  )
                })}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-border"
              />
              User is active
            </label>

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            {success ? (
              <p className="text-sm text-primary" role="status">
                {success}
              </p>
            ) : null}

            <Button type="submit" disabled={submitting || loading || form.branch_ids.length === 0}>
              {submitting ? 'Saving…' : creating ? 'Create user' : 'Save user'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
